/**
 * MCP Read Tool Handlers
 *
 * Implementations for read-only tools that inspect the IDF model.
 */

import type { IDFDocument, IDFObject } from '../../parser';
import type { IDDSchema, IDDObjectType } from '../../idd';
import type {
  MCPToolResult,
  ObjectInfo,
  SchemaInfo,
  ModelStatistics,
} from '../types';

/**
 * Context provided to all handlers
 */
export interface HandlerContext {
  document: IDFDocument | null;
  schema: IDDSchema | null;
  getObjectById: (id: string) => IDFObject | undefined;
}

/**
 * Get object name from first field
 */
function getObjectName(obj: IDFObject): string | null {
  return obj.fields[0]?.value || null;
}

/**
 * Convert IDFObject to ObjectInfo for API response
 */
function objectToInfo(obj: IDFObject, schema: IDDSchema | null): ObjectInfo {
  const schemaType = schema?.objectTypes.get(obj.className.toLowerCase());

  const info: ObjectInfo = {
    id: obj.id,
    className: obj.className,
    name: getObjectName(obj),
    fieldCount: obj.fields.length,
    fields: obj.fields.map((field, index) => {
      const schemaField = schemaType?.fields[index];
      const result: { index: number; name: string; value: string; type: string; units?: string } = {
        index,
        name: schemaField?.name || `Field ${String(index + 1)}`,
        value: field.value,
        type: schemaField?.type || 'unknown',
      };
      const units = schemaField?.units || field.units;
      if (units) {
        result.units = units;
      }
      return result;
    }),
  };
  if (obj.sourceLocation) {
    info.sourceLocation = obj.sourceLocation;
  }
  return info;
}

/**
 * Convert IDDObjectType to SchemaInfo for API response
 */
function schemaTypeToInfo(schemaType: IDDObjectType): SchemaInfo {
  return {
    name: schemaType.name,
    group: schemaType.group,
    memo: schemaType.memo,
    isUnique: schemaType.isUnique,
    isRequired: schemaType.isRequired,
    isObsolete: schemaType.isObsolete,
    minFields: schemaType.minFields,
    extensible: schemaType.extensible,
    fields: schemaType.fields.map((field) => {
      const fieldInfo: {
        id: string;
        name: string;
        type: string;
        required: boolean;
        memo: string;
        default?: string;
        units?: string;
        minimum?: number;
        maximum?: number;
        choices?: string[];
      } = {
        id: field.id,
        name: field.name,
        type: field.type,
        required: field.required,
        memo: field.memo,
      };
      if (field.default !== undefined) fieldInfo.default = field.default;
      if (field.units !== undefined) fieldInfo.units = field.units;
      if (field.minimum !== undefined) fieldInfo.minimum = field.minimum;
      if (field.maximum !== undefined) fieldInfo.maximum = field.maximum;
      if (field.choices !== undefined) fieldInfo.choices = field.choices;
      return fieldInfo;
    }),
  };
}

/**
 * Handle get_model_summary tool
 */
export function handleGetModelSummary(
  _args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const doc = ctx.document;
  const objectsByClass = new Map<string, number>();
  const zones: string[] = [];
  const airLoops: string[] = [];
  const plantLoops: string[] = [];
  const surfaces = { walls: 0, floors: 0, roofs: 0, windows: 0, doors: 0 };
  let schedules = 0;
  let materials = 0;
  let constructions = 0;
  let zonalEquipment = 0;

  for (const obj of doc.objects) {
    const className = obj.className;
    const lowerClass = className.toLowerCase();
    objectsByClass.set(className, (objectsByClass.get(className) || 0) + 1);

    // Categorize objects
    if (lowerClass === 'zone') {
      const name = getObjectName(obj);
      if (name) zones.push(name);
    } else if (lowerClass === 'airloophvac') {
      const name = getObjectName(obj);
      if (name) airLoops.push(name);
    } else if (lowerClass.startsWith('plantloop')) {
      const name = getObjectName(obj);
      if (name) plantLoops.push(name);
    } else if (lowerClass.includes('surface')) {
      if (lowerClass.includes('wall') || obj.fields[1]?.value.toLowerCase() === 'wall') {
        surfaces.walls++;
      } else if (lowerClass.includes('floor') || obj.fields[1]?.value.toLowerCase() === 'floor') {
        surfaces.floors++;
      } else if (lowerClass.includes('roof') || lowerClass.includes('ceiling') ||
                 obj.fields[1]?.value.toLowerCase() === 'roof' || obj.fields[1]?.value.toLowerCase() === 'ceiling') {
        surfaces.roofs++;
      }
    } else if (lowerClass.includes('window') || lowerClass.includes('fenestration')) {
      surfaces.windows++;
    } else if (lowerClass.includes('door')) {
      surfaces.doors++;
    } else if (lowerClass.startsWith('schedule')) {
      schedules++;
    } else if (lowerClass === 'material' || lowerClass.startsWith('material:')) {
      materials++;
    } else if (lowerClass === 'construction' || lowerClass.startsWith('construction:')) {
      constructions++;
    } else if (lowerClass.startsWith('zonehvac:')) {
      zonalEquipment++;
    }
  }

  const stats: ModelStatistics = {
    version: doc.version,
    totalObjects: doc.objects.length,
    objectsByClass: Object.fromEntries(objectsByClass),
    zones,
    surfaces,
    hvac: {
      airLoops,
      plantLoops,
      zonalEquipment,
    },
    schedules,
    materials,
    constructions,
  };

  return { success: true, content: stats };
}

/**
 * Handle get_objects_by_class tool
 */
export function handleGetObjectsByClass(
  args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const className = args["className"] as string;
  const limit = (args["limit"] as number) || 50;

  const objects = ctx.document.objects
    .filter((obj) => obj.className.toLowerCase() === className.toLowerCase())
    .slice(0, limit)
    .map((obj) => objectToInfo(obj, ctx.schema));

  return {
    success: true,
    content: {
      className,
      count: objects.length,
      objects,
    },
  };
}

/**
 * Handle get_object_by_id tool
 */
export function handleGetObjectById(
  args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const objectId = args["objectId"] as string;
  const obj = ctx.getObjectById(objectId);

  if (!obj) {
    return { success: false, error: `Object not found: ${objectId}` };
  }

  return {
    success: true,
    content: objectToInfo(obj, ctx.schema),
  };
}

/**
 * Handle get_object_by_name tool
 */
export function handleGetObjectByName(
  args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const objectName = (args["objectName"] as string).toLowerCase();
  const className = args["className"] as string | undefined;

  const obj = ctx.document.objects.find((o) => {
    const name = getObjectName(o);
    if (!name || name.toLowerCase() !== objectName) return false;
    if (className && o.className.toLowerCase() !== className.toLowerCase()) return false;
    return true;
  });

  if (!obj) {
    return { success: false, error: `Object not found: ${String(args["objectName"])}` };
  }

  return {
    success: true,
    content: objectToInfo(obj, ctx.schema),
  };
}

/**
 * Handle get_field_value tool
 */
export function handleGetFieldValue(
  args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const objectId = args["objectId"] as string;
  const fieldIndex = args["fieldIndex"] as number;

  const obj = ctx.getObjectById(objectId);
  if (!obj) {
    return { success: false, error: `Object not found: ${objectId}` };
  }

  const field = obj.fields[fieldIndex];
  if (!field) {
    return { success: false, error: `Field index ${String(fieldIndex)} out of range` };
  }

  const schemaType = ctx.schema?.objectTypes.get(obj.className.toLowerCase());
  const schemaField = schemaType?.fields[fieldIndex];

  return {
    success: true,
    content: {
      objectId,
      fieldIndex,
      value: field.value,
      fieldName: schemaField?.name || `Field ${String(fieldIndex + 1)}`,
      type: schemaField?.type || 'unknown',
      units: schemaField?.units,
      default: schemaField?.default,
      minimum: schemaField?.minimum,
      maximum: schemaField?.maximum,
      choices: schemaField?.choices,
      required: schemaField?.required,
      memo: schemaField?.memo,
    },
  };
}

/**
 * Handle get_object_schema tool
 */
export function handleGetObjectSchema(
  args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.schema) {
    return { success: false, error: 'No schema loaded' };
  }

  const className = args["className"] as string;
  const schemaType = ctx.schema.objectTypes.get(className.toLowerCase());

  if (!schemaType) {
    return { success: false, error: `Unknown object type: ${className}` };
  }

  return {
    success: true,
    content: schemaTypeToInfo(schemaType),
  };
}

/**
 * Handle list_object_classes tool
 */
export function handleListObjectClasses(
  _args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const counts = new Map<string, number>();
  for (const obj of ctx.document.objects) {
    counts.set(obj.className, (counts.get(obj.className) || 0) + 1);
  }

  const classes = Array.from(counts.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);

  return {
    success: true,
    content: {
      totalClasses: classes.length,
      classes,
    },
  };
}

/**
 * Handle get_zones tool
 */
export function handleGetZones(
  _args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const zones = ctx.document.objects
    .filter((obj) => obj.className.toLowerCase() === 'zone')
    .map((obj) => {
      return {
        id: obj.id,
        name: obj.fields[0]?.value || 'Unnamed',
        directionOfRelativeNorth: obj.fields[1]?.value,
        xOrigin: obj.fields[2]?.value,
        yOrigin: obj.fields[3]?.value,
        zOrigin: obj.fields[4]?.value,
        type: obj.fields[5]?.value,
        multiplier: obj.fields[6]?.value,
        ceilingHeight: obj.fields[7]?.value,
        volume: obj.fields[8]?.value,
        floorArea: obj.fields[9]?.value,
      };
    });

  return {
    success: true,
    content: {
      count: zones.length,
      zones,
    },
  };
}

/**
 * Handle get_zone_surfaces tool
 */
export function handleGetZoneSurfaces(
  args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const zoneName = (args["zoneName"] as string).toLowerCase();

  // Find surfaces associated with this zone
  const surfaces = ctx.document.objects.filter((obj) => {
    const lowerClass = obj.className.toLowerCase();
    if (!lowerClass.includes('surface') && !lowerClass.includes('fenestration') &&
        !lowerClass.includes('window') && !lowerClass.includes('door')) {
      return false;
    }

    // Check zone name field (usually field 3 for BuildingSurface:Detailed)
    for (const field of obj.fields) {
      if (field.value.toLowerCase() === zoneName) {
        return true;
      }
    }
    return false;
  });

  return {
    success: true,
    content: {
      zoneName: args["zoneName"],
      count: surfaces.length,
      surfaces: surfaces.map((obj) => ({
        id: obj.id,
        className: obj.className,
        name: getObjectName(obj),
        surfaceType: obj.fields[1]?.value,
        constructionName: obj.fields[2]?.value,
      })),
    },
  };
}

/**
 * Handle get_hvac_overview tool
 */
export function handleGetHvacOverview(
  _args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const airLoops = ctx.document.objects
    .filter((obj) => obj.className.toLowerCase() === 'airloophvac')
    .map((obj) => ({
      id: obj.id,
      name: getObjectName(obj),
    }));

  const plantLoops = ctx.document.objects
    .filter((obj) => obj.className.toLowerCase().startsWith('plantloop'))
    .map((obj) => ({
      id: obj.id,
      name: getObjectName(obj),
      className: obj.className,
    }));

  const zoneEquipment = ctx.document.objects
    .filter((obj) => obj.className.toLowerCase().startsWith('zonehvac:'))
    .map((obj) => ({
      id: obj.id,
      name: getObjectName(obj),
      className: obj.className,
    }));

  const thermostats = ctx.document.objects
    .filter((obj) => obj.className.toLowerCase().includes('thermostat'))
    .map((obj) => ({
      id: obj.id,
      name: getObjectName(obj),
      className: obj.className,
    }));

  return {
    success: true,
    content: {
      airLoops: {
        count: airLoops.length,
        items: airLoops,
      },
      plantLoops: {
        count: plantLoops.length,
        items: plantLoops,
      },
      zoneEquipment: {
        count: zoneEquipment.length,
        items: zoneEquipment.slice(0, 20),
      },
      thermostats: {
        count: thermostats.length,
        items: thermostats,
      },
    },
  };
}

/**
 * Handle list_schema_object_types tool
 */
export function handleListSchemaObjectTypes(
  args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.schema) {
    return { success: false, error: 'No schema loaded' };
  }

  const groupFilter = args["group"] as string | undefined;
  const searchQuery = (args["searchQuery"] as string | undefined)?.toLowerCase();

  let groups = ctx.schema.groups;

  if (groupFilter) {
    groups = groups.filter((g) => g.name.toLowerCase().includes(groupFilter.toLowerCase()));
  }

  const result = groups.map((group) => {
    let objectTypes = group.objectTypes;

    if (searchQuery) {
      objectTypes = objectTypes.filter((name) => name.toLowerCase().includes(searchQuery));
    }

    return {
      groupName: group.name,
      objectTypes: objectTypes.slice(0, 50),
      totalInGroup: objectTypes.length,
    };
  }).filter((g) => g.objectTypes.length > 0);

  return {
    success: true,
    content: {
      schemaVersion: ctx.schema.version,
      groupCount: result.length,
      groups: result,
    },
  };
}

/**
 * All read handlers
 */
export const readHandlers: Record<
  string,
  (args: Record<string, unknown>, ctx: HandlerContext) => MCPToolResult
> = {
  get_model_summary: handleGetModelSummary,
  get_objects_by_class: handleGetObjectsByClass,
  get_object_by_id: handleGetObjectById,
  get_object_by_name: handleGetObjectByName,
  get_field_value: handleGetFieldValue,
  get_object_schema: handleGetObjectSchema,
  list_object_classes: handleListObjectClasses,
  get_zones: handleGetZones,
  get_zone_surfaces: handleGetZoneSurfaces,
  get_hvac_overview: handleGetHvacOverview,
  list_schema_object_types: handleListSchemaObjectTypes,
};
